/**
 * Customer Lookup — DigniFi Call Center Tool
 * 
 * A clean, Google-style search interface for DigniFi call center reps.
 * No account list — just search and results.
 */

import React, { useState, useEffect, useRef } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search, Loader2, ChevronUp, Phone, PhoneIncoming,
  XCircle, ArrowLeft, MessageSquare
} from "lucide-react";
import { trpc } from "@/lib/trpc";

// ============================================
// Helpers
// ============================================
function formatDateTime(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    const date = new Date(d + ' EDT');
    if (isNaN(date.getTime())) {
      const fallback = new Date(d);
      if (isNaN(fallback.getTime())) return String(d);
      return fallback.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' }) + 
        ' ' + fallback.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' }) + 
      ' ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  } catch { return String(d); }
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return String(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return String(d); }
}

function formatCurrency(n: number | string | null | undefined): string {
  if (n === null || n === undefined) return '—';
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(num)) return '—';
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  const last10 = digits.length > 10 ? digits.slice(-10) : digits;
  if (last10.length === 10) {
    return `(${last10.slice(0,3)}) ${last10.slice(3,6)}-${last10.slice(6)}`;
  }
  return phone;
}

// Status badge
function getStatusBadge(status: string | null | undefined) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'call answered') {
    return <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-xs font-medium">Contacted</Badge>;
  }
  if (s === 'voicemail') {
    return <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30 text-xs font-medium">Voicemail</Badge>;
  }
  if (s === 'no answer' || s === 'no_answer') {
    return <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-xs font-medium">No Answer</Badge>;
  }
  if (s === 'declined' || s === 'short call') {
    return <Badge className="bg-rose-500/15 text-rose-400 border-rose-500/30 text-xs font-medium">Declined</Badge>;
  }
  if (s === 'not_called' || s === 'queued' || s === 'pending' || s === 'none' || !status) {
    return <Badge className="bg-gray-500/15 text-gray-400 border-gray-500/30 text-xs font-medium">Pending</Badge>;
  }
  return <Badge variant="outline" className="text-xs">{status}</Badge>;
}

// Call outcome badge for timeline
function getOutcomeBadge(reason: string | null | undefined) {
  const r = String(reason || '').toLowerCase();
  if (r === 'agent_hangup' || r === 'user_hangup') {
    return <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-xs">Conversation</Badge>;
  }
  if (r === 'voicemail_reached') {
    return <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30 text-xs">Voicemail</Badge>;
  }
  if (r === 'no_answer' || r === 'dial_no_answer') {
    return <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-xs">No Answer</Badge>;
  }
  if (r === 'user_declined') {
    return <Badge className="bg-rose-500/15 text-rose-400 border-rose-500/30 text-xs">Declined</Badge>;
  }
  return <Badge variant="outline" className="text-xs">{reason || '—'}</Badge>;
}

// Direction icon
function CallDirectionIcon({ toNumber }: { toNumber?: string }) {
  const isInbound = toNumber && (toNumber.includes('7257126046') || toNumber.includes('7027613617'));
  if (isInbound) {
    return <PhoneIncoming className="h-3.5 w-3.5 text-blue-400" />;
  }
  return <Phone className="h-3.5 w-3.5 text-emerald-400" />;
}

// ============================================
// Contact History Panel
// ============================================
function ContactHistory({ loanNumber }: { loanNumber: string }) {
  const { data, isLoading } = trpc.dignifi.accountContactHistory.useQuery({ loanNumber });

  if (isLoading) {
    return (
      <div className="py-8 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading history...</span>
      </div>
    );
  }

  const lead = data?.lead;
  const calls = data?.calls || [];
  const smsMessages = data?.smsMessages || [];

  return (
    <div className="space-y-5">
      {/* Customer Info Card */}
      {lead && (
        <div className="rounded-xl border border-border/40 bg-muted/5 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-red-500/15 flex items-center justify-center">
              <span className="text-red-400 font-bold text-sm">
                {String(lead.full_name || '?').charAt(0)}
              </span>
            </div>
            <div>
              <h3 className="font-semibold text-base">{lead.full_name}</h3>
              <p className="text-sm text-muted-foreground">{formatPhone(lead.phone)}</p>
            </div>
            <div className="ml-auto">
              {getStatusBadge(lead.call_status)}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Loan #</p>
              <p className="font-mono font-medium">{lead.loan_number}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Dealer</p>
              <p className="font-medium truncate">{lead.dealer}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Amount</p>
              <p className="font-medium">{formatCurrency(lead.loan_amount)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">First Payment</p>
              <p className="font-medium">{formatDate(lead.first_due_date)}</p>
            </div>
          </div>
        </div>
      )}

      {/* SMS Conversations */}
      {smsMessages.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <MessageSquare className="h-3.5 w-3.5 text-violet-400" />
            SMS Conversation
            <span className="text-muted-foreground font-normal">
              — {smsMessages.length} {smsMessages.length === 1 ? 'message' : 'messages'}
            </span>
          </h4>
          <div className="rounded-xl border border-border/40 p-4 space-y-3 max-h-80 overflow-y-auto">
            {smsMessages.map((msg: any, i: number) => {
              const isOutbound = msg.status === 'sent';
              return (
                <div key={i} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-xl px-3.5 py-2.5 text-sm ${
                    isOutbound
                      ? 'bg-violet-500/15 border border-violet-500/25 text-violet-100'
                      : 'bg-muted/30 border border-border/40 text-foreground'
                  }`}>
                    <p className="leading-relaxed">{msg.message_body}</p>
                    <p className={`text-[10px] mt-1.5 ${isOutbound ? 'text-violet-400/70' : 'text-muted-foreground/70'}`}>
                      {formatDateTime(msg.sent_at)} {isOutbound ? '• Sent' : '• Received'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Outreach Timeline */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500"></span>
          Outreach Timeline
          <span className="text-muted-foreground font-normal">
            — {calls.length} {calls.length === 1 ? 'attempt' : 'attempts'}
          </span>
        </h4>

        {calls.length === 0 ? (
          <div className="rounded-xl border border-border/40 p-6 text-center">
            <p className="text-sm text-muted-foreground">No outreach attempts recorded yet</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {calls.map((call: any, i: number) => {
              const isInbound = call.to_number && (call.to_number.includes('7257126046') || call.to_number.includes('7027613617'));
              return (
                <div key={i} className="rounded-xl border border-border/40 p-4 hover:bg-muted/10 transition-colors">
                  {/* Row 1: Time, direction, outcome, duration */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2.5">
                      <CallDirectionIcon toNumber={call.to_number} />
                      <span className="text-sm font-medium">{formatDateTime(call.call_time)}</span>
                      {getOutcomeBadge(call.disconnection_reason)}
                      {isInbound && (
                        <span className="text-xs text-blue-400 font-medium bg-blue-500/10 px-2 py-0.5 rounded-full">Inbound</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {call.call_duration && (
                        <span className="text-xs text-muted-foreground font-mono bg-muted/20 px-2 py-0.5 rounded">{call.call_duration}</span>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Audio player */}
                  {call.recording_url && (
                    <div className="mt-3">
                      <audio
                        controls
                        preload="none"
                        className="w-full h-9"
                        style={{ filter: 'invert(1) hue-rotate(180deg)', opacity: 0.85 }}
                      >
                        <source src={call.recording_url} type="audio/wav" />
                        <source src={call.recording_url} type="audio/mpeg" />
                        Your browser does not support audio playback.
                      </audio>
                    </div>
                  )}

                  {/* Row 3: Call summary */}
                  {call.detailed_call_summary && (
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed bg-muted/5 rounded-lg p-3 border border-border/20">
                      {call.detailed_call_summary}
                    </p>
                  )}

                  {/* Row 4: Key findings */}
                  {(call.aware_of_dignifi || call.aware_of_first_payment || call.opted_in_to_auto_pay) && (
                    <div className="flex flex-wrap gap-2.5 mt-3">
                      {call.aware_of_dignifi && (
                        <span 
                          title={call.aware_of_dignifi === 'yes' ? 'Consumer confirmed they knew DigniFi is their lender' : 'Consumer was not aware DigniFi is their lender'}
                          className={`text-xs px-2.5 py-1 rounded-full border cursor-help ${
                          call.aware_of_dignifi === 'yes' 
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                            : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                        }`}>
                          {call.aware_of_dignifi === 'yes' ? '✓' : '✗'} Knew about DigniFi
                        </span>
                      )}
                      {call.aware_of_first_payment && (
                        <span 
                          title={call.aware_of_first_payment === 'yes' ? 'Consumer confirmed they know their first payment date' : 'Consumer was not aware of their first payment date'}
                          className={`text-xs px-2.5 py-1 rounded-full border cursor-help ${
                          call.aware_of_first_payment === 'yes' 
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                            : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                        }`}>
                          {call.aware_of_first_payment === 'yes' ? '✓' : '✗'} Knew payment date
                        </span>
                      )}
                      {call.opted_in_to_auto_pay && call.opted_in_to_auto_pay !== 'Unknown' && (
                        <span 
                          title={call.opted_in_to_auto_pay === 'yes' ? 'Consumer has autopay set up on their account' : 'Consumer does NOT have autopay set up — may need follow-up'}
                          className={`text-xs px-2.5 py-1 rounded-full border cursor-help ${
                          call.opted_in_to_auto_pay === 'yes' 
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                            : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                        }`}>
                          {call.opted_in_to_auto_pay === 'yes' ? '✓ Autopay' : '✗ Autopay'}
                        </span>
                      )}
                      {call.dealer_feedback && call.dealer_feedback !== 'None' && call.dealer_feedback !== 'N/A' && (
                        <span 
                          title="Feedback the consumer shared about their dealership experience"
                          className="text-xs px-2.5 py-1 rounded-full border cursor-help bg-purple-500/10 text-purple-400 border-purple-500/20">
                          Dealer: "{call.dealer_feedback}"
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Main Page
// ============================================
export default function LoanServicing() {
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [selectedLoan, setSelectedLoan] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Only query when there's an active search
  const { data: accountsData, isLoading } = trpc.dignifi.servicingAccounts.useQuery({
    search: activeSearch || undefined,
    status: undefined,
    page,
    pageSize: 10,
    sortBy: 'created_at',
    sortOrder: 'desc',
  }, {
    enabled: !!activeSearch,
  });

  const accounts = accountsData?.accounts || [];
  const totalAccounts = accountsData?.total || 0;

  // Handle search submit
  const handleSearch = () => {
    if (search.trim()) {
      setActiveSearch(search.trim());
      setSelectedLoan(null);
      setPage(1);
    }
  };

  // Handle back to search
  const handleBackToSearch = () => {
    setActiveSearch('');
    setSearch('');
    setSelectedLoan(null);
    setPage(1);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // No active search — show centered hero search
  if (!activeSearch) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="w-full max-w-xl space-y-8 -mt-16">
            
            {/* DigniFi Logo */}
            <div className="flex justify-center">
              <div className="bg-black rounded-2xl px-8 py-5">
                <img 
                  src="/dignifi-logo.png" 
                  alt="DigniFi" 
                  className="h-12 w-auto"
                />
              </div>
            </div>

            {/* Title */}
            <div className="text-center space-y-2">
              <h1 className="text-3xl font-bold tracking-tight">Customer Lookup</h1>
              <p className="text-muted-foreground">
                Search by name, phone number, or loan number
              </p>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                ref={inputRef}
                placeholder="e.g. John Smith, (555) 123-4567, or 834922"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-14 pr-14 h-14 text-lg rounded-2xl border-border/60 focus:border-red-500/50 focus:ring-red-500/20 bg-muted/10 shadow-lg shadow-black/5"
                autoFocus
              />
              {search && (
                <button 
                  onClick={() => setSearch('')}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Search Button */}
            <div className="flex justify-center">
              <Button 
                onClick={handleSearch}
                disabled={!search.trim()}
                className="px-8 h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium shadow-lg shadow-red-900/20 transition-all"
              >
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>

            {/* Subtle footer */}
            <p className="text-center text-xs text-muted-foreground/50 pt-4">
              Powered by UnifiPro AI
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Active search — show results
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-5 space-y-4">

        {/* Top bar: back + search */}
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleBackToSearch}
            className="h-9 px-3 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back
          </Button>
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && search.trim()) {
                  setActiveSearch(search.trim());
                  setSelectedLoan(null);
                  setPage(1);
                }
              }}
              className="pl-10 h-10 rounded-xl border-border/50 bg-muted/10"
            />
          </div>
          <Button 
            onClick={() => { setActiveSearch(search.trim()); setSelectedLoan(null); setPage(1); }}
            disabled={!search.trim()}
            size="sm"
            className="h-10 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white"
          >
            Search
          </Button>
        </div>

        {/* Results count */}
        <p className="text-sm text-muted-foreground px-1">
          {isLoading ? 'Searching...' : `${totalAccounts.toLocaleString()} ${totalAccounts === 1 ? 'result' : 'results'} for "${activeSearch}"`}
        </p>

        {/* Results */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-red-400" />
            <span className="ml-3 text-muted-foreground">Searching...</span>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No customers found matching "{activeSearch}"</p>
            <p className="text-sm text-muted-foreground/60 mt-1">Try a different name, phone number, or loan number</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {accounts.map((acct: any, idx: number) => {
              const isSelected = selectedLoan === String(acct.loan_number);
              return (
                <Card key={acct.id || acct.loan_number || idx} className={`border-border/40 overflow-hidden transition-all ${isSelected ? 'ring-1 ring-red-500/30' : ''}`}>
                  <CardContent className="p-0">
                    {/* Account Row */}
                    <div
                      className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-muted/10 transition-colors"
                      onClick={() => setSelectedLoan(isSelected ? null : String(acct.loan_number))}
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="h-9 w-9 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
                          <span className="text-red-400 font-bold text-xs">
                            {String(acct.full_name || '?').charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{String(acct.full_name ?? '—')}</span>
                            {getStatusBadge(acct.call_status)}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                            <span className="font-mono">#{String(acct.loan_number ?? '—')}</span>
                            <span>·</span>
                            <span className="truncate max-w-[250px]">{String(acct.dealer ?? '')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {acct.last_called_at && (
                          <span className="text-xs text-muted-foreground hidden sm:inline">
                            Last contact: {formatDateTime(acct.last_called_at)}
                          </span>
                        )}
                        <ChevronUp className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isSelected ? '' : 'rotate-180'}`} />
                      </div>
                    </div>

                    {/* Expanded Contact History */}
                    {isSelected && (
                      <div className="px-5 py-5 border-t border-border/20 bg-muted/5">
                        <ContactHistory loanNumber={String(acct.loan_number)} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
